// dashboard card surfacing the latest Full + Lite SEACURE(TOOL) release
// with one-click download buttons. visual shell mirrors the Round 2
// pentest-activity cards (rounded-lg bg-slate-50 + tiny uppercase header
// + Refresh icon + "View all" link).
//
// editions aren't stored as a column, they're encoded in the
// installer filename (`SEACURE(TOOL)_Lite_Setup_*.exe` is Lite,
// `SEACURE(TOOL)_Setup_*.exe` is Full). we fetch the 10 newest releases
// and pick the first match per edition so cadence skew between Full
// and Lite uploads doesn't lose either side.
//
// download flow lives in @/lib/releaseDownload (shared with the
// /pentest-releases page). R2-assembled releases resolve to a presigned URL
// and download natively in the browser; older releases fall back to the
// chunk-by-chunk fetch + client-side concat.

import React, { useEffect, useState } from 'react'
import { Download, Loader, RefreshCw, Package } from 'lucide-react'
import { Link } from 'react-router-dom'
import { projectsApi, type ReleaseRow } from '@/lib/projectsApi'
import { downloadRelease, type ReleaseDownloadMode } from '@/lib/releaseDownload'
import { useToast } from '@/contexts/ToastContext'

type Edition = 'full' | 'lite'

function editionOf(filename: string): Edition {
  return filename?.includes('_Lite_Setup_') ? 'lite' : 'full'
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
}

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000)
  if (!isFinite(seconds) || seconds < 0) return new Date(isoString).toLocaleDateString()
  if (seconds < 60)        return 'just now'
  if (seconds < 3600)      return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400)     return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 86400 * 30) return `${Math.floor(seconds / 86400)}d ago`
  return new Date(isoString).toLocaleDateString()
}

export const LatestReleaseCard: React.FC = () => {
  const { success, error: showError } = useToast()
  const [releases, setReleases] = useState<ReleaseRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [downloadMode, setDownloadMode] = useState<ReleaseDownloadMode | null>(null)
  const [progress, setProgress] = useState(0)
  // server-computed availability (R2 key or chunk files on disk). fail-open:
  // if the fetch fails, buttons stay enabled as before
  const [availability, setAvailability] = useState<Record<number, boolean> | null>(null)
  const isAvailable = (id: number) => availability?.[id] !== false

  const load = async () => {
    setLoading(true)
    try {
      const data = await projectsApi.listRecentReleases(10)
      setReleases(Array.isArray(data) ? data : [])
      try {
        const rows = await projectsApi.getReleaseAvailability()
        const map: Record<number, boolean> = {}
        for (const r of rows) map[r.id] = r.available
        setAvailability(map)
      } catch (err) {
        console.error('[LatestReleaseCard] availability error:', err)
      }
    } catch (err) {
      console.error('[LatestReleaseCard] load error:', err)
      setReleases([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const latestFull = releases?.find(r => editionOf(r.installer_file_name) === 'full') ?? null
  const latestLite = releases?.find(r => editionOf(r.installer_file_name) === 'lite') ?? null

  const handleDownload = async (release: ReleaseRow) => {
    if (downloadingId === release.id) return
    if (!isAvailable(release.id)) {
      showError('Unavailable', 'Installer files for this release are no longer stored.')
      return
    }
    setDownloadingId(release.id)
    setProgress(0)

    try {
      const mode = await downloadRelease(
        release,
        (loaded, total) => {
          setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0)
        },
        setDownloadMode,
      )
      if (mode === 'r2') {
        success(`Download started: ${release.installer_file_name}`)
      } else {
        success(`Downloaded ${release.installer_file_name}`)
      }
    } catch (err: any) {
      console.error('[LatestReleaseCard] download error:', err)
      showError('Download failed', err?.message ?? 'Try again from the Pentest Releases page.')
    } finally {
      setDownloadingId(null)
      setDownloadMode(null)
      setProgress(0)
    }
  }

  return (
    <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase text-slate-400">
          Latest Release
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
            className="h-6 shrink-0 px-1.5 text-xs inline-flex items-center rounded-md border border-slate-200 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to="/pentest-releases"
            className="text-[10px] uppercase text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            View all →
          </Link>
        </div>
      </div>

      {!releases ? (
        <div className="py-6 text-center text-xs text-slate-400">
          <Loader className="h-4 w-4 animate-spin mx-auto mb-1" />
          Loading…
        </div>
      ) : releases.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">
          <Package className="h-5 w-5 mx-auto mb-1 text-slate-300 dark:text-slate-600" />
          No releases yet
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
          {([
            { label: 'Full', release: latestFull },
            { label: 'Lite', release: latestLite },
          ] as const).map(({ label, release }) => (
            <div
              key={label}
              className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-2 flex flex-col"
            >
              <div className="flex items-baseline justify-between mb-0.5">
                <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                  {label}
                </span>
                <span className="text-[10px] text-slate-400">
                  {release ? timeAgo(release.created_at) : '—'}
                </span>
              </div>
              {release ? (
                <>
                  <div className="text-xs font-mono text-slate-600 dark:text-slate-300 tabular-nums">
                    v{release.version}
                  </div>
                  <div className="text-[10px] text-slate-400 mb-1.5">
                    {formatFileSize(release.installer_file_size)}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownload(release)}
                    disabled={downloadingId === release.id || !isAvailable(release.id)}
                    title={isAvailable(release.id) ? undefined : 'Installer files for this release are no longer stored'}
                    className="mt-auto inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {downloadingId === release.id ? (
                      <>
                        <Loader className="h-3 w-3 animate-spin" />
                        {progress > 0 ? `${progress}%` : 'Starting…'}
                      </>
                    ) : (
                      <><Download className="h-3 w-3" /> Download</>
                    )}
                  </button>
                  {!isAvailable(release.id) && (
                    <p className="mt-1.5 text-[11px] leading-snug text-amber-600 dark:text-amber-500">
                      Unavailable — installer files no longer stored
                    </p>
                  )}
                  {downloadingId === release.id && downloadMode === 'chunks' && (
                    <p className="mt-1.5 text-[11px] leading-snug text-amber-600 dark:text-amber-500">
                      Large file — this may take a few minutes.
                    </p>
                  )}
                </>
              ) : (
                <div className="text-xs text-slate-400 italic flex-1 flex items-center justify-center">
                  No {label.toLowerCase()} release
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
